import React from 'react';

import { getAffiliateError } from '../api';

/** A single affiliate read: its data, load flag, a human error + machine code, and a manual reload. */
export interface AffiliateResource<T> {
  data?: T;
  loading: boolean;
  error?: string;
  /** Machine-readable code (e.g. `AFFILIATES_EMAIL_NOT_VERIFIED`) for branching the UI state. */
  errorCode?: string;
  /** Read again. Resolves once that read has answered (or failed), so a caller can wait for the fresh data. */
  reload: () => Promise<void>;
}

/**
 * Load one affiliate resource for the current brand, mirroring the marketing-page
 * data-hook shape (channel-detail): it stays in `loading` until a `casinoInstanceId`
 * resolves, re-runs when the brand changes, and a stale in-flight response is ignored
 * (latest request wins) so a brand switch never paints the previous brand's data.
 *
 * `fetcher` must be stable across renders, pass a module function or wrap it in
 * `useCallback` (it closes over the brand id via the argument, not the closure).
 */
export function useAffiliateResource<T>(
  casinoInstanceId: string | undefined,
  fetcher: (casinoInstanceId: string) => Promise<T>,
): AffiliateResource<T> {
  const [reloadTick, setReloadTick] = React.useState(0);
  // Callers waiting on a reload: settled by the read that reload started (or the read that superseded it), or at
  // unmount, so a caller awaiting a reload (to toast after it) is never left hanging when its component goes away.
  const waiters = React.useRef<Array<() => void>>([]);
  const mounted = React.useRef(true);
  const [state, setState] = React.useState<{ data?: T; loading: boolean; error?: string; errorCode?: string }>({
    loading: true,
  });

  React.useEffect(() => {
    // No brand resolved yet (initial load / mid brand-switch fade): hold in loading.
    if (!casinoInstanceId) {
      setState({ loading: true });
      return;
    }
    let active = true;
    let settled = false;
    const mine = waiters.current;
    waiters.current = [];
    const settle = (): void => {
      settled = true;
      mine.forEach((resolve) => resolve());
    };
    setState((prev) => ({ ...prev, loading: true, error: undefined, errorCode: undefined }));
    fetcher(casinoInstanceId)
      .then((data) => {
        if (!active) return;
        setState({ data, loading: false });
        settle();
      })
      .catch((err: unknown) => {
        if (!active) return;
        const { message, code } = getAffiliateError(err);
        setState({ loading: false, error: message, errorCode: code });
        settle();
      });
    return () => {
      active = false;
      // A superseded read hands its waiters to the read replacing it, so they resolve on the fresh data.
      if (!settled) waiters.current = [...mine, ...waiters.current];
    };
  }, [casinoInstanceId, fetcher, reloadTick]);

  // Declared after the read effect on purpose: on unmount its cleanup runs after the read's, so it also settles the
  // waiters a superseded read handed back.
  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      const pending = waiters.current;
      waiters.current = [];
      pending.forEach((resolve) => resolve());
    };
  }, []);

  const reload = React.useCallback(
    () =>
      new Promise<void>((resolve) => {
        // Nothing will read again once unmounted: settle at once.
        if (!mounted.current) {
          resolve();
          return;
        }
        waiters.current.push(resolve);
        setReloadTick((tick) => tick + 1);
      }),
    [],
  );
  return { ...state, reload };
}
